import type { Provider } from '@angular/core';
import { InjectionToken, Optional, SkipSelf } from '@angular/core';

/**
 * DI token holding the merged map of inlined SVG markup, keyed by `fl-icons.ts`
 * export name (e.g. `flTrophy`). Consumed by `<fl-icon>` to render without an
 * HTTP fetch when the icon is registered; unregistered names throw in non-prod
 * and fall back to the HTTP fetch path in prod.
 *
 * Registered via `provideFlIcons`, which merges ancestor registrations through
 * a `skipSelf` factory so a shared primitive (e.g. `fl-view-header`) providing
 * its own icons never shadows an ancestor's — mirrors @ng-icons' `provideIcons`.
 */
export const FL_ICONS = new InjectionToken<Record<string, string>>('FL_ICONS', {
  providedIn: 'root',
  factory: () => ({}),
});

/**
 * Registers inlined SVG markup so `<fl-icon>` in this component or its
 * descendants can resolve it. Merges with ancestor
 * registrations rather than shadowing them.
 *
 * @example
 *   providers: [provideFlIcons({ flTrophy, flAccount })]
 */
export function provideFlIcons(icons: Record<string, string>): Provider[] {
  return [
    {
      provide: FL_ICONS,
      useFactory: (ancestorIcons: Record<string, string> | null) => ({
        ...ancestorIcons,
        ...icons,
      }),
      deps: [[new SkipSelf(), new Optional(), FL_ICONS]],
    },
  ];
}
